// libs
import React, { memo, useCallback, useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router-dom';

// components
import Price from './components/Price';
import Badge from './components/Badge';
import MultiTags from './components/MultiTags';

// helpers
import { getAkamaiUrl } from 'shared/helpers/utils';
import { isOurURL, getCleanUrl } from 'shared/helpers/utils/url';
import { storeAnalyticsDataInLocal } from 'shared/helpers/storage/storeData';

// constants
import { TEXT, WISHLIST_ICON_SIZE } from './constants';

// analytics
import { onProductClick } from './analytics';

// styles
import {
  ProductAnchor,
  ProductImage,
  Description,
  Title,
  SubTitle,
  SoldOut,
  SizeSoldOut,
  Sizes,
  Placeholder,
  imgClassName,
  DyanamicText,
  CustomWislist,
} from './styles';

// defs
interface IProductProps {
  item: Listing.IProduct;
  width?: number;
  animateImage?: boolean;
  aspectRatio?: number;
  openInTab?: boolean;
  className?: string;
  showSizes?: boolean;
  imageContain?: boolean;
  onInteraction?: VoidFunction;
  isFloatingTags: boolean;
  showWishlist?: boolean;
  imageChildren?: React.ReactNode;
  descriptionChildren?: React.ReactNode;
}
function Product({
  item,
  width,
  className,
  animateImage = true,
  aspectRatio = 0.75,
  openInTab = false,
  showSizes = true,
  imageContain = false,
  onInteraction,
  isFloatingTags,
  showWishlist = false,
  imageChildren,
  descriptionChildren,
}: IProductProps) {
  const [showImage, setShowImage] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (showImage) {
      return;
    }

    if (width !== undefined) {
      setShowImage(true);
    }
  }, [showImage, width]);

  const goTo = useCallback(
    (e) => {
      e.preventDefault();
      let URL = item.actionUrl || item.action_url || '';

      // data will be accessed via Launch Layer
      if (item.lcp !== undefined) {
        storeAnalyticsDataInLocal({ lcp: item.lcp });
      }

      // analytics
      onProductClick(item);

      onInteraction && onInteraction();

      if (openInTab) {
        window.open(URL);
        return;
      }

      if (isOurURL(URL)) {
        history.push(getCleanUrl(URL));
      } else {
        window.location.href = URL;
      }
    },
    [history, item, onInteraction, openInTab]
  );

  // sizeVariation can be null
  const sizeVariation = item.sizeVariation || [];
  const canShowSizes = showSizes && sizeVariation.length > 0;
  const showSizeOOS =
    Boolean(item.isOutOfStock) === false &&
    Boolean(item.isSizeOutOfStock) === true &&
    isEmpty(item.messageSizeOutOfStock) == false;

  const containsDynamicText = isEmpty(item.dynamic_text) === false;

  return (
    <ProductAnchor
      className={className}
      href={getCleanUrl(item.actionUrl || item.action_url || '')}
      aria-label={`${item.title} - ${item.subTitle}`}
      onClick={goTo}
      data-test="product-anchor"
    >
      {showImage ? (
        <section className="product__image">
          <Badge value={item.badge} isDesktop={openInTab} />
          <ProductImage
            src={getAkamaiUrl(item.imageUrl, { width: width * 2 })}
            width={width}
            aspectRatio={aspectRatio}
            animate={animateImage}
            alt={`${item.title} - ${item.subTitle}`}
            data-test="image-component"
            imageContain={imageContain}
            imgClassName={imgClassName}
          />
          {isEmpty(item.tag) === false && isFloatingTags === true && (
            <MultiTags
              dataTest="floating-tags"
              tagData={item.tag}
              isFloatingTags={isFloatingTags}
            />
          )}

          {showWishlist && (
            <CustomWislist
              size={WISHLIST_ICON_SIZE}
              productId={item.id}
              price={item.price}
              isOutOfStock={item.isOutOfStock}
              displayWishlistMessage={true}
            />
          )}

          {imageChildren !== undefined && imageChildren}
        </section>
      ) : (
        <Placeholder data-test="product-placeholder" />
      )}
      <Description className="product__description">
        {Boolean(item.isOutOfStock) === true && (
          <SoldOut data-test="product-soldOut">{TEXT.SOLD_OUT}</SoldOut>
        )}
        {showSizeOOS && (
          <SizeSoldOut data-test="product-sizeSoldOut">{item.messageSizeOutOfStock}</SizeSoldOut>
        )}

        {isEmpty(item.tag) === false && isFloatingTags === false && (
          <MultiTags dataTest="non-floating-tags" tagData={item.tag} />
        )}

        <Title data-test="product-title">{item.title}</Title>
        <SubTitle data-test="product-subTitle">{item.subTitle}</SubTitle>
        <Price
          discount={item.discount}
          discountedPrice={item.discountedPrice}
          price={item.price}
          hidePercentage={false}
        />

        {canShowSizes && (
          <Sizes data-test="product-sizes">
            {sizeVariation
              .map((itemChild: Listing.IProductSizeVariation) => itemChild.title)
              .join(', ')}
          </Sizes>
        )}

        {containsDynamicText && (
          <DyanamicText {...item.dynamic_text}>{item.dynamic_text.title}</DyanamicText>
        )}

        {descriptionChildren !== undefined && descriptionChildren}
      </Description>
    </ProductAnchor>
  );
}

export default memo(Product);
